import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect, useRef } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useAdminLandingView } from "../../hooks/useAdminLandingView";
import { useAdminSpaceView } from "../../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../../hooks/useAdminViewSubject";
import { writeChatPanelOpen } from "../../lib/chatPanelOpen";
import { handOffPendingPersonalChat } from "../../lib/pendingPersonalChats";
import { navigate } from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { Workflow } from "../Workflow";
import {
  WorkflowBackRowSkeleton,
  WorkflowNavigationSkeleton,
} from "../WorkflowNavigation";
import { ChatPageSurface } from "./ChatPageSurface";

// Opening a workflow version lands in the chat that already works on it, or a
// new chat on it, so a workflow is always met as a conversation.
export function WorkflowChatEntry({
  spaceId,
  workflowId,
  branchId,
  step,
}: {
  spaceId: string;
  workflowId: string;
  branchId?: string;
  step?: string;
}) {
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const adminLanding = useAdminLandingView();
  if (adminSubject || adminSpace || adminLanding)
    return <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />;
  return <OpenWorkflowChat workflowId={workflowId} branchId={branchId} />;
}

function OpenWorkflowChat({
  workflowId,
  branchId,
}: {
  workflowId: string;
  branchId?: string;
}) {
  const z = useZero();
  const [chats, result] = useQuery(
    queries.personalChatSummaries({ includeClosed: true })
  );
  const [workflow, workflowResult] = useQuery(
    queries.loadWorkflow({ workflowId })
  );
  // The version the workflow page opens, which is the first draft when nothing is live yet.
  const [version, versionResult] = useQuery(
    queries.loadBranchMetadata({ workflowId, branchId })
  );
  const versionBranchId =
    version && version.name !== "main" ? version.id : null;
  const versionBranchName = versionBranchId ? version?.name : undefined;
  const ownerId =
    result.type === "complete" &&
    workflowResult.type === "complete" &&
    versionResult.type === "complete"
      ? (chats
          .filter(
            (chat) =>
              chat.workflowId === workflowId &&
              chat.branchId === versionBranchId
          )
          .toSorted((a, b) => b.lastActiveAt - a.lastActiveAt)[0]?.id ?? null)
      : undefined;
  const openedRef = useRef(false);
  useEffect(() => {
    if (ownerId === undefined || openedRef.current) return;
    openedRef.current = true;
    writeChatPanelOpen(true);
    const id = ownerId ?? nanoid(12);
    if (!ownerId) {
      const created = { id, chatId: nanoid(12) };
      z.mutate(sharedMutators.createPersonalChat(created));
      handOffPendingPersonalChat(created);
    }
    // Recorded now, so opening the same version again finds this chat, and it rises to the top of the list.
    if (workflow)
      z.mutate(
        sharedMutators.setChatFocus({
          id,
          entryId: nanoid(12),
          workflowId,
          branchId: versionBranchId,
          workflowName: workflow.name,
          branchName: versionBranchName ?? null,
        })
      );
    navigate(null, { chatId: id, navigation: "source" }, { replace: true });
  }, [ownerId, z, workflow, workflowId, versionBranchId, versionBranchName]);
  // The chat page’s own frame, so arriving reads as one continuous transition.
  return (
    <Backdrop
      permanentNavigation
      inChat
      headerContent={<WorkflowBackRowSkeleton />}
      navigation={<WorkflowNavigationSkeleton />}
    >
      <ChatPageSurface />
    </Backdrop>
  );
}
